/**
 * Format API error responses for display
 * Handles various error formats including Pydantic validation errors
 *
 * @param {Error} err - The error object from axios
 * @param {string} fallbackMessage - Default message if error can't be parsed
 * @returns {string} Formatted error message
 */
export const formatErrorMessage = (err, fallbackMessage = 'An error occurred') => {
  console.error('Error details:', err);

  // Check for response data
  if (err.response?.data?.detail) {
    const detail = err.response.data.detail;

    // Handle Pydantic validation errors (array of error objects)
    if (Array.isArray(detail)) {
      const errors = detail.map(e => {
        // e.loc is typically ['body', 'field_name'] or ['field_name']
        const field = e.loc?.slice(1).join('.') || e.loc?.join('.') || 'Field';
        const message = e.msg || 'Invalid value';
        return `${field}: ${message}`;
      });

      // Return first 3 errors to avoid overwhelming the user
      if (errors.length > 3) {
        return errors.slice(0, 3).join('; ') + ` (and ${errors.length - 3} more)`;
      }
      return errors.join('; ');
    }

    // Handle string detail
    if (typeof detail === 'string') {
      return detail;
    }

    // Handle object detail (try to extract message)
    if (typeof detail === 'object') {
      const base = detail.message || detail.detail || null;

      // Some endpoints attach the records that blocked the action (e.g. role
      // revocation returns the user's open CAPAs / permits). Spell them out so
      // the user knows what to reassign instead of just being refused.
      if (Array.isArray(detail.open_items) && detail.open_items.length > 0) {
        // Acronyms and multi-word types don't survive a naive slug-to-words
        // conversion, so name the known ones explicitly.
        const TYPE_LABELS = {
          capa: ['CAPA', 'CAPAs'],
          permit: ['permit', 'permits'],
          risk_assessment: ['risk assessment', 'risk assessments'],
          document_approval: ['document approval', 'document approvals'],
          incident: ['incident', 'incidents'],
        };
        const summary = detail.open_items
          .map(item => {
            const count = Number(item.count) || 0;
            const key = String(item.type || 'item');
            const fallback = key.replace(/_/g, ' ');
            const [one, many] = TYPE_LABELS[key] || [fallback, `${fallback}s`];
            return `${count} open ${count === 1 ? one : many}`;
          })
          .join(', ');
        return base ? `${base} (${summary})` : summary;
      }

      return base || JSON.stringify(detail);
    }
  }

  // Check for alternative message formats
  if (err.response?.data?.message) {
    return err.response.data.message;
  }

  // Check for error message property
  if (err.message) {
    return err.message;
  }

  // Return fallback
  return fallbackMessage;
};

/**
 * Format validation errors for display in forms
 * Returns an object mapping field names to error messages
 *
 * @param {Error} err - The error object from axios
 * @returns {Object} Object mapping field names to error messages
 */
export const formatValidationErrors = (err) => {
  const errors = {};

  if (err.response?.data?.detail && Array.isArray(err.response.data.detail)) {
    err.response.data.detail.forEach(e => {
      // Extract field name (skip 'body' prefix if present)
      const fieldPath = e.loc?.slice(1) || e.loc || [];
      const field = fieldPath.join('.');

      if (field) {
        errors[field] = e.msg || 'Invalid value';
      }
    });
  }

  return errors;
};

/**
 * Check if error is a validation error
 * @param {Error} err - The error object
 * @returns {boolean}
 */
export const isValidationError = (err) => {
  return err.response?.status === 422 ||
         (Array.isArray(err.response?.data?.detail) &&
          err.response?.data?.detail[0]?.type);
};

/**
 * Check if error is an authentication error
 * @param {Error} err - The error object
 * @returns {boolean}
 */
export const isAuthError = (err) => {
  return err.response?.status === 401 || err.response?.status === 403;
};
